import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { createShare, getUserShares, removeShare, acceptShare, declineShare } from '../services/sharingService';
import './ShareCalendar.css';

function ShareCalendar({ refreshSharedUsers }) {
  const { currentUser } = useAuth();
  const [email, setEmail] = useState('');
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch existing shares
  useEffect(() => {
    if (!currentUser) return;

    const fetchShares = async () => {
      try {
        const userShares = await getUserShares(currentUser.uid, currentUser.email);
        setShares(userShares);
      } catch (err) {
        console.error('Error fetching shares:', err);
      }
    };

    fetchShares();
  }, [currentUser]);

  const handleShare = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await createShare(currentUser.uid, currentUser.email, email);
      
      if (result.error) {
        setError(result.error);
      } else {
        setSuccess(`Calendar shared with ${email}`);
        setEmail('');
        
        // Refresh shares list
        const userShares = await getUserShares(currentUser.uid, currentUser.email);
        setShares(userShares);
      }
    } catch (err) {
      setError('Failed to share calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveShare = async (shareId) => {
    try {
      await removeShare(shareId);
      const userShares = await getUserShares(currentUser.uid, currentUser.email);
      setShares(userShares);
    } catch (err) {
      setError('Failed to remove share');
    }
  };

  const handleAcceptShare = async (shareId) => {
    try {
      const result = await acceptShare(shareId, currentUser.uid);
      const userShares = await getUserShares(currentUser.uid, currentUser.email);
      setShares(userShares);
      setSuccess('Share accepted');
      
      // Add a small delay to ensure Firestore has propagated the changes
      setTimeout(async () => {
        // Refresh shared users to update event queries
        if (refreshSharedUsers) {
          await refreshSharedUsers();
        }
      }, 500);
    } catch (err) {
      console.error('Error accepting share:', err);
      setError(`Failed to accept share: ${err.message}`);
    }
  };

  const handleAcceptAndShareBack = async (shareId, ownerEmail) => {
    try {
      // First accept the incoming share
      await acceptShare(shareId, currentUser.uid);
      
      // Then create a reverse share (share current user's calendar with the owner)
      const reverseShareResult = await createShare(currentUser.uid, currentUser.email, ownerEmail);
      
      if (reverseShareResult.error) {
        console.warn('Reverse share failed:', reverseShareResult.error);
        // Still show success for the main acceptance even if reverse share fails
        setSuccess('Share accepted (bidirectional sharing may already exist)');
      } else {
        setSuccess('Share accepted and your calendar shared back - bidirectional access enabled!');
      }
      
      const userShares = await getUserShares(currentUser.uid, currentUser.email);
      setShares(userShares);
      
      // Add a small delay to ensure Firestore has propagated the changes
      setTimeout(async () => {
        // Refresh shared users to update event queries
        if (refreshSharedUsers) {
          await refreshSharedUsers();
        }
      }, 500);
    } catch (err) {
      console.error('Error accepting share and sharing back:', err);
      setError(`Failed to accept share: ${err.message}`);
    }
  };

  const handleDeclineShare = async (shareId) => {
    try {
      await declineShare(shareId);
      const userShares = await getUserShares(currentUser.uid, currentUser.email);
      setShares(userShares);
      setSuccess('Share declined');
    } catch (err) {
      console.error('Error declining share:', err);
      setError(`Failed to decline share: ${err.message}`);
    }
  };

  // Separate shares into different categories
  const myShares = shares.filter(s => s.ownerUserId === currentUser.uid);
  const sharedWithMe = shares.filter(s => s.sharedWithEmail === currentUser.email || s.sharedWithUserId === currentUser.uid);

  return (
    <div className="share-calendar-container">
      <h2>Share Calendar</h2>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="share-form">
        <h3>Share your calendar</h3>
        <form onSubmit={handleShare}>
          <input
            type="email"
            placeholder="Enter email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <button type="submit" disabled={loading}>
            {loading ? 'Sharing...' : 'Share'}
          </button>
        </form>
      </div>

      <div className="shares-list">
        <h3>People you've shared with</h3>
        {myShares.length === 0 ? (
          <p>You haven't shared your calendar with anyone yet.</p>
        ) : (
          <ul>
            {myShares.map(share => (
              <li key={share.id}>
                <span>{share.sharedWithEmail}</span>
                <span className={`status ${share.status}`}>{share.status}</span>
                <button onClick={() => handleRemoveShare(share.id)}>Remove</button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="shares-list">
        <h3>Calendars shared with you</h3>
        {sharedWithMe.length === 0 ? (
          <p>No calendars have been shared with you.</p>
        ) : (
          <ul>
            {sharedWithMe.map(share => (
              <li key={share.id}>
                <span>From: {share.ownerEmail || 'A user'}</span>
                <span className={`status ${share.status}`}>{share.status}</span>
                {share.status === 'pending' && (
                  <div className="action-buttons">
                    <button onClick={() => handleAcceptShare(share.id)}>Accept</button>
                    <button 
                      onClick={() => handleAcceptAndShareBack(share.id, share.ownerEmail)}
                      className="accept-bidirectional-btn"
                      title="Accept and share your calendar back for two-way access"
                    >
                      Accept & Share Back
                    </button>
                    <button onClick={() => handleDeclineShare(share.id)}>Decline</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default ShareCalendar;